/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React from 'react';
import { CreatorProfileSummary } from '../../api';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import CreatorProfileModal from './CreatorProfileModal';

interface CreatorDirectoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creators: CreatorProfileSummary[];
}

export const CreatorDirectoryDialog: React.FC<CreatorDirectoryDialogProps> = ({
  open,
  onOpenChange,
  creators,
}) => {
  const [selectedId, setSelectedId] = React.useState<number | null>(null);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl">
          <DialogTitle>Contributors</DialogTitle>
          <div className="text-sm text-muted-foreground mb-4">
            Meet the artists and instructors collaborating with Guituitive.
          </div>
          <ScrollArea className="max-h-[60vh]">
            <div className="grid gap-4 sm:grid-cols-2">
              {creators.map((creator) => (
                <button
                  key={creator.creatorProfileId}
                  className="flex items-center gap-4 rounded-lg border border-border bg-card p-4 text-left hover:bg-accent"
                  onClick={() => setSelectedId(creator.creatorProfileId)}
                >
                  <img
                    src={creator.avatarUrl || '/logo192.png'}
                    alt={creator.displayName}
                    className="h-14 w-14 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold">{creator.displayName}</p>
                    <p className="text-xs text-muted-foreground">
                      {creator.websiteUrl ?? creator.slug}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
      {selectedId && (
        <CreatorProfileModal
          creatorProfileId={selectedId}
          open={true}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setSelectedId(null);
            }
          }}
        />
      )}
    </>
  );
};
